import './PermissionsList.scss';
import { Header } from '../Header/Header';
import { Mode, ISharingInformation, ISharingLink, ISharingPrincipal, SharingAudience, ClientId } from '../../interfaces/SharingInterfaces';
import { ShareLink } from './ShareLink/ShareLink';
import { SharePrincipal } from './SharePrincipal/SharePrincipal';
import { SharePrincipal2 } from './SharePrincipal/SharePrincipal2';
import { ShareViewState } from '../Share/Share';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import * as ClientIdHelper from '../../utilities/ClientIdHelper';
import * as React from 'react';
import stringsResx from '../Share.resx';
import { Spinner, SpinnerType } from 'office-ui-fabric-react/lib/Spinner';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost, DirectionalHint } from 'office-ui-fabric-react/lib/Tooltip';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import Features from '@ms/odsp-utilities/lib/features/Features';
import * as ShareCommunicationManager from '../../utilities/ShareCommunicationManager';
import { autobind, getRTLSafeKeyCode, KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import * as PropTypes from 'prop-types';

// Features
declare const window: Window & {
  _sharingContextInfo?: { webAbsoluteUrl: string; };
};

// Enable UI on specific Heartbeat Tenant.
const isShareHeartbeatTenant = (window._sharingContextInfo && window._sharingContextInfo.webAbsoluteUrl) ?
  (window._sharingContextInfo.webAbsoluteUrl.toLowerCase().indexOf('a830edad9050849880j18121110') >= 0) : false;

const EnableLinkSettingsEditPanel = { ODB: 778, OneDrive: isShareHeartbeatTenant };
export interface IPermissionsListProps {
  mode: Mode;
  clientId: ClientId;
  scenarioId: string;
  companyName: string;
  sharingInformation: ISharingInformation;
  groupsMemberCount?: number;
  linkRecipients?: Array<IPerson>; // List of recipients that'll receive email with link (not necessarily permissioned).
  viewState: ShareViewState;
  backButtonClicked: () => void;
  hideElementsInManageAccess: (visible: boolean) => void;
}

export interface IPermissionsListState {
  viewState: ShareViewState;
  showLinkDetails: boolean;
  currentSharingLink: ISharingLink;
}

export class PermissionsList extends React.Component<IPermissionsListProps, IPermissionsListState> {
  private _isLinkSettingsEditPanelEnabled: boolean;
  private _isManageAccessModeLoaded = false;
  private _shareClickTime: number;

  static contextTypes = {
    shareClickTime: PropTypes.number
  };

  public constructor(props: IPermissionsListProps, context: any) {
    super(props);

    this._shareClickTime = context.shareClickTime;

    this.state = {
      viewState: this.props.viewState,
      showLinkDetails: false,
      currentSharingLink: undefined
    };

    this._isLinkSettingsEditPanelEnabled = ClientIdHelper.isTeams(this.props.clientId) || ((this.props.mode === Mode.manageAccess || this.props.mode === Mode.share) && Features.isFeatureEnabled(EnableLinkSettingsEditPanel));
  }

  public componentDidUpdate() {
    // Hide the stop sharing button and advanced link if link details is visible.
    this.props.hideElementsInManageAccess(this.state.showLinkDetails);
  }

  public render(): React.ReactElement<{}> {
    const sharingInformation = this.props.sharingInformation;
    const hasActiveSharingLinks = this.props.sharingInformation.sharingLinks.reduce((count, sharingLink: ISharingLink) => {
      return count + (sharingLink.isActive ? 1 : 0);
    }, -1) > 0;

    let content;
    if (!sharingInformation.hasPermissionsInformation) {
      content = (
        <div className='od-Share-spinnerHolder'>
          <Spinner
            className='od-Share-spinner'
            label={ stringsResx.componentLoading }
            type={ SpinnerType.large }
          />
        </div>
      );
    } else if (sharingInformation.isShared) {
      content = (
        <div className='od-PermissionsList-content'>
          <div className='od-PermissionsList-links'>{ this._renderLinks() }</div>
          <div className='od-PermissionsList-entities'>{ this._renderPrincipals(hasActiveSharingLinks) }</div>
        </div>
      );
    } else {
      content = (
        <div>
          { stringsResx.notShared }
        </div>
      );
    }

    if (!this._isManageAccessModeLoaded && this.props.mode === Mode.manageAccess) {
      ShareCommunicationManager.pageFinishedLoading(this._shareClickTime, this.props.clientId, this.props.mode, this.props.scenarioId);
      this._isManageAccessModeLoaded = true;
    }

    const showHeader: boolean = !ClientIdHelper.isODSP(this.props.clientId) && !this.state.showLinkDetails;
    return (
      <div className='od-PermissionsList'>
        { showHeader && this._renderHeader() }
        { this.state.showLinkDetails ? this._renderLinkDetails() : content }
      </div>
    );
  }

  private _renderHeader(): JSX.Element {
    return (
      <div className='od-PermissionsList-header'>
        { this.props.mode !== Mode.manageAccess && <IconButton
          className='od-Share-backButton'
          onClick={ this.props.backButtonClicked }
          iconProps={ { iconName: 'Back' } }
          ariaLabel={ stringsResx.backButtonLabel }
        >
          <label className='od-Share-screenReaderOnly'>{ stringsResx.backButtonLabel }</label>
        </IconButton> }
        <Header
          clientId={ this.props.clientId }
          item={ this.props.sharingInformation.item }
          viewState={ ShareViewState.permissionsList }
          data-is-focusable={ true }
          mode={ this.props.mode }
        />
      </div>
    );
  }

  private _renderLinkDetails(): JSX.Element {
    return (
      <ShareLink
        key={ this.state.currentSharingLink.shareId }
        mode={ this.props.mode }
        companyName={ this.props.companyName }
        link={ this.state.currentSharingLink }
        clientId={ this.props.clientId }
        sharingInformation={ this.props.sharingInformation }
        groupsMemberCount={ this.props.groupsMemberCount }
        linkRecipients={ this.props.linkRecipients }
        enableLinkSettings={ this._isLinkSettingsEditPanelEnabled }
        showMoreOptions={ false }
        onShowLinkDetailsClicked={ this._onLinkSelected }
        linkDetailsView={ true } // Indicates link details view.
        showPermissionsList={ this._showPermissionsList }
      />
    );
  }

  @autobind
  private _showPermissionsList() {
    this.setState({
      showLinkDetails: false
    });
  }

  private _renderLinks(): JSX.Element {
    const links = this.props.sharingInformation.sharingLinks;
    let linksReturned: boolean = false;
    return (
      <div className='od-PermissionsList-linksListHolder'>
        { this._isLinkSettingsEditPanelEnabled &&
          <div className='od-ShareLinkDetails--sectionHeader' tabIndex={ 0 } aria-label={ stringsResx.linksListHeaderTooltip }>
            <Icon iconName={ 'Link' } className={ 'ms-Icon ms-Icon--Link' } />
            { stringsResx.linksListHeader }
            <TooltipHost
              id={ 'od-ShareLinkDetails-tooltip-links' }
              content={ stringsResx.linksListHeaderTooltip }
              directionalHint={ DirectionalHint.topCenter }
              calloutProps={ { gapSpace: 0 } }
              tooltipProps={ { maxWidth: '300px' } }
            >
              <Icon iconName={ 'Info' } className={ 'ms-Icon ms-Icon--Info' } aria-describedby={ 'od-ShareLinkDetails-tooltip-links' } aria-haspopup={ true } />
            </TooltipHost>
          </div>
        }
        <ul
          className='od-PermissionsList-links'
        >
          { links.map((link: ISharingLink, index: number) => {
            // Don't display direct link.
            if (link.audience !== SharingAudience.specificPeople && link.isActive) {
              linksReturned = true;
              return (
                <li key={ link.shareId } >
                  <ShareLink
                    key={ link.shareId }
                    mode={ this.props.mode }
                    companyName={ this.props.companyName }
                    link={ link }
                    clientId={ this.props.clientId }
                    sharingInformation={ this.props.sharingInformation }
                    groupsMemberCount={ this.props.groupsMemberCount }
                    linkRecipients={ this.props.linkRecipients } // List of recipients that'll receive email with link (not necessarily permissioned).
                    enableLinkSettings={ this._isLinkSettingsEditPanelEnabled }
                    showMoreOptions={ this._isLinkSettingsEditPanelEnabled }
                    onShowLinkDetailsClicked={ this._onLinkSelected }
                    linkDetailsView={ false }
                    showPermissionsList={ this._showPermissionsList }
                  />
                </li>
              );
            }
          }) }
          { !linksReturned && this._renderNoSharingLinks() }
        </ul>
      </div>
    );
  }

  private _renderNoSharingLinks() {
    if (this._isLinkSettingsEditPanelEnabled) {
      return (
        <div aria-live='assertive' role='alert' aria-atomic='true' className="od-PermissionsList-emptyLinks">
          { stringsResx.noLinksGivingAccess }
        </div>
      );
    }
  }

  @autobind
  private _onLinkSelected(link: ISharingLink) {
    this.setState({
      currentSharingLink: link,
      showLinkDetails: true
    });
  }

  private _renderPrincipals(hasActiveSharingLinks: boolean): JSX.Element {
    const sharingInformation = this.props.sharingInformation;

    // Get direct principals.
    let principals = sharingInformation.sharingPrincipals.slice();

    // Merge list of principals together.
    return (this._isLinkSettingsEditPanelEnabled ?
      <div className='od-PermissionsList-entitiesListHolder'>
        { this._isLinkSettingsEditPanelEnabled &&
          <div className='od-ShareLinkDetails--sectionHeader' tabIndex={ 0 } aria-label={ stringsResx.principalsListHeaderTooltip }>
            <Icon iconName={ 'PlayerSettings' } className={ 'ms-Icon ms-Icon--PlayerSettings' } />
            { stringsResx.principalsListHeader }
            <TooltipHost
              id='od-ShareLinkDetails-tooltip-principals'
              content={ stringsResx.principalsListHeaderTooltip }
              directionalHint={ DirectionalHint.topCenter }
              calloutProps={ { gapSpace: 0 } }
              tooltipProps={ { maxWidth: '300px' } }
            >
              <Icon iconName={ 'Info' } className={ 'ms-Icon ms-Icon--Info' } aria-describedby={ 'od-ShareLinkDetails-tooltip-principals' } aria-haspopup={ true } />
            </TooltipHost>
          </div>
        }
        <ul
          className='od-PermissionsList-entities'
          aria-label={ stringsResx.permissionsListPrincipalsAriaLabel }
        >
          <FocusZone direction={ FocusZoneDirection.vertical }
            isInnerZoneKeystroke={ (ev) => (ev.which === getRTLSafeKeyCode(KeyCodes.right)) }>
            { principals.map((principal: ISharingPrincipal, index: number) => {
              return (
                <li key={ principal.id } >
                  <SharePrincipal2
                    key={ principal.id }
                    principal={ principal }
                    userRole={ sharingInformation.userRole }
                    clientId={ this.props.clientId }
                    appmap={ this.props.sharingInformation.item.appmap }
                  />
                </li>
              );
            }) }
          </FocusZone>
        </ul>
      </div>
      :
      <ul
        className='od-PermissionsList-entities'
        role='list'
        aria-label={ stringsResx.permissionsListPrincipalsAriaLabel }
      >
        { principals.map((principal: ISharingPrincipal, index: number) => {
          return (
            <li
              key={ principal.id }
              tabIndex={ 0 }
              aria-atomic={ true }
              aria-live='assertive'
              className='od-PermissionsList-principalGroup'>
              <SharePrincipal
                principal={ principal }
                takeFocus={ !hasActiveSharingLinks && index === 0 }
                userRole={ sharingInformation.userRole }
                clientId={ this.props.clientId }
                appmap={ this.props.sharingInformation.item.appmap }
              />
            </li>
          );
        }) }
      </ul>
    );
  }
}
